/**
 * WebSocket Provider
 *
 * 인증 상태에 따라 WebSocket 연결을 관리합니다.
 * - 로그인 시: 자동 연결 및 조직 Room 입장
 * - 로그아웃 시: 연결 해제
 * - 조직 변경 시: Room 전환
 * - 멤버 이벤트 처리 (초대, 수락, 제거)
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { useWebSocketStore } from '../stores/websocket';
import { useToastStore } from '../stores/toast';
import { useNotificationStore, mapServerTypeToLocalType } from '../stores/notification';
import { useInspectionHighlightStore } from '../stores/inspectionHighlight';
import { useInspectionListRefreshStore } from '../stores/inspectionListRefresh';
import { useUsageStore } from '../stores/usage';
import { useEntityChangedStore } from '../stores/entityChanged';
import { registerServiceWorker, subscribeToPush } from '../lib/push-notifications';
import { tokenManager } from '../lib/api';
import { resolveEntityQueryKeys, resolveEntityDeletedNavigation } from '../lib/entityChanged';
import { todayScheduledQueryRootKey } from '../lib/inspections';
import {
  LEGAL_OBLIGATION_INSTANCE_RESOURCE_TYPE,
  LEGAL_OBLIGATION_SITE_RESOURCE_TYPE,
  legalObligationInstancePath,
  legalSiteObligationsPath,
} from '../lib/legalDocumentRoutes';
import { AGENCY_LINK_REQUEST_RESOURCE_TYPE, agencyLinkRequestPath } from '../lib/agencySiteLinkView';
import {
  ServerEvents,
  EntityChangedPayload,
  PhotoAddedPayload,
  PhotoDeletedPayload,
  PhotosDeletedPayload,
  PhotoAnalysisCompletedPayload,
  PhotoAnalysisFailedPayload,
  PhotoReviewUpdatedPayload,
  PhotosBulkReviewedPayload,
  FindingAddedPayload,
  FindingUpdatedPayload,
  FindingResolvedPayload,
  FindingDeletedPayload,
  FindingRestoredPayload,
  FindingMeasureAddedPayload,
  FindingMeasureRemovedPayload,
  FindingLegalAddedPayload,
  FindingLegalRemovedPayload,
  InspectionReviewCompletedPayload,
  InspectionDeletedPayload,
  InspectionCreatedPayload,
  InspectionStatusChangedPayload,
  InspectionUpdatedPayload,
  InspectionContributorJoinedRoomPayload,
  InspectionContributorLeftRoomPayload,
  InspectionContributorReviewCompletedPayload,
  InspectionContributorReviewCancelledPayload,
  NotificationReadPayload,
  NotificationAllReadPayload,
  NotificationClearedPayload,
  InvitationRespondedPayload,
  InvitationCancelledPayload,
  InvitationRejectedPayload,
  ActionResultPayload,
  InspectionModifiedPayload,
  InspectionContributorJoinedPayload,
  InspectionSoftDeletedPayload,
  InspectionRestoredPayload,
  InspectionAllCompletedPayload,
  InspectionCompletedNotifyPayload,
  ReportCreatedNotifyPayload,
  ReportDeletedNotifyPayload,
  PreviousInspectionCompletedPayload,
  AIAnalysisStartedPayload,
  AIAnalysisCompletedPayload,
  AIAnalysisFailedPayload,
  UsageUpdatedPayload,
  OrganizationVerificationStatusChangedPayload,
} from '../types/websocket';

// MEMBER_LEFT 전용 형상 — shared `MemberEventPayload`의 퇴장 경로 부분집합이다.
// `reason` 값은 서버 emit 자리와 1:1로 맞춘다(`organizations.service.ts`,
// `organization-closed-notification.util.ts`) — 하나라도 빠지면 그 분기가 조용히 죽는다.
interface MemberRemovedPayload {
  companyId: string;
  organizationName: string;
  userId: string;
  userName: string;
  reason: 'removed' | 'member_removed' | 'left' | 'self_left' | 'organization_deleted';
}

interface MemberInvitedPayload {
  invitationId: string;
  companyId: string;
  organizationName: string;
  inviterName: string;
  siteId?: string;
  siteName?: string;
}

interface MemberJoinedPayload {
  companyId: string;
  organizationName: string;
  userId: string;
  userName: string;
  invitationId: string;
}

/** 소유자 지정(소유권 이전) 통지 — P7-b-2: 옛 `oldRole`/`newRole`(enum) 대신 소유자 성질 하나 */
interface MemberRoleChangedPayload {
  companyId: string;
  organizationName: string;
  userId: string;
  isOwner: boolean;
  reason?: 'ownership_transferred' | 'role_changed';
}

interface OrganizationRestoredPayload {
  companyId: string;
  organizationName: string;
  restoredBy?: 'owner' | 'admin';
}

interface SiteMemberAssignedPayload {
  siteId: string;
  siteName: string;
  companyId: string;
  organizationName: string;
  memberId: string;
  userId: string;
  userName: string;
  assignedBy?: string;
}

interface SitePrimaryNeededPayload {
  siteId: string;
  siteName: string;
  companyId: string;
  organizationName: string;
  removedUserName?: string;
}

interface SiteUpdatedPayload {
  siteId: string;
  companyId: string;
  siteName: string;
  updatedBy: string;
  updatedByUserId?: string;
}

interface SiteAssignmentChangedPayload {
  siteId: string;
  siteName: string;
  companyId: string;
  organizationName: string;
  assignmentType: 'PRIMARY' | 'SECONDARY';
  changedBy: string;
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated: isUserAuthenticated, currentMembership: authCurrentMembership, fetchUser, refreshSession, user, setCurrentOrganization } = useAuthStore();
  // WS Room 식별에는 companyId를 사용 (H-8b: backend WS room = company:ID)
  const currentCompanyId = authCurrentMembership?.companyId ?? null;
  // 개별 selector로 가져와서 참조 안정성 확보
  const connect = useWebSocketStore((state) => state.connect);
  const disconnect = useWebSocketStore((state) => state.disconnect);
  const switchOrganization = useWebSocketStore((state) => state.switchOrganization);
  const isConnected = useWebSocketStore((state) => state.isConnected);
  const registerHandler = useWebSocketStore((state) => state.registerHandler);
  const hasSubscription = useWebSocketStore((state) => state.hasSubscription);
  const { showToast } = useToastStore();
  const { addNotification, syncMarkAsRead, syncMarkAllAsRead, syncClearAll, fetchFromServer: fetchNotificationsFromServer, initializeForUser } = useNotificationStore();
  const { addHighlight } = useInspectionHighlightStore();
  const { triggerRefresh: triggerInspectionListRefresh } = useInspectionListRefreshStore();
  const { updateFromSocket: updateUsageFromSocket } = useUsageStore();
  const bumpEntityVersion = useEntityChangedStore((state) => state.bump);
  const prevOrgIdRef = useRef<string | null>(null);
  const pushSubscribedRef = useRef(false);
  // 최근 처리한 이벤트 추적 (중복 방지용) - {eventType}:{resourceId} 형태로 저장
  const processedEventsRef = useRef<Map<string, number>>(new Map());
  const DUPLICATE_THRESHOLD_MS = 2000; // 2초 내 같은 이벤트는 무시

  // 중복 이벤트 체크 헬퍼 - true면 이미 처리됨 (무시해야 함)
  const isDuplicateEvent = useCallback((eventType: string, resourceId: string): boolean => {
    const key = `${eventType}:${resourceId}`;
    const lastProcessed = processedEventsRef.current.get(key);
    const now = Date.now();

    if (lastProcessed && now - lastProcessed < DUPLICATE_THRESHOLD_MS) {
      console.log(`[WebSocket] Duplicate event ignored: ${key}`);
      return true;
    }

    processedEventsRef.current.set(key, now);

    // 오래된 항목 정리 (10초 이상 지난 항목)
    for (const [k, timestamp] of processedEventsRef.current.entries()) {
      if (now - timestamp > 10000) {
        processedEventsRef.current.delete(k);
      }
    }

    return false;
  }, []);

  // 이벤트 핸들러 refs - 구독은 안정적으로 유지하면서 항상 최신 핸들러 호출
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlersRef = useRef<Record<string, (data: any) => void>>({});

  // 개인 조직(내 작업 공간) 찾기 헬퍼
  const findPersonalOrganization = useCallback(() => {
    const currentUser = useAuthStore.getState().user;
    // H-9c: category 기반으로 전환 (registrationNumber 직접 참조 제거)
    return currentUser?.memberships.find((m) => m.company.category === 'WORKSPACE');
  }, []);

  // 멤버 제거/탈퇴 이벤트 핸들러
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleMemberRemoved = useCallback(async (data: MemberRemovedPayload) => {
    // DEBUG: 이벤트 수신 확인
    console.log('[WebSocket] MEMBER_LEFT received:', { reason: data.reason, companyId: data.companyId, userId: data.userId });

    // 본인이 Owner에 의해 제거된 경우 (emitToUser로 본인에게만 전송됨)
    if (data.reason === 'removed') {
      // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
      // 사용자 정보 새로고침 (멤버십 목록 업데이트)
      await fetchUser();

      // 제거된 조직이 현재 조직인 경우 개인 조직으로 전환
      if (data.companyId === currentCompanyId) {
        const personalMembership = findPersonalOrganization();
        if (personalMembership?.companyId) {
          setCurrentOrganization(personalMembership.companyId);
        }
        navigate('/');
      }
      return;
    }

    // 본인이 자발적으로 탈퇴한 경우 - 다른 세션 동기화 (emitToUser로 본인에게만 전송됨)
    // 토스트/알림/네비게이션은 ACTION_RESULT (org:left)에서 처리됨
    if (data.reason === 'self_left') {
      // 사용자 정보 새로고침만 수행 (조직 전환과 네비게이션은 ACTION_RESULT에서 처리)
      await fetchUser();
      return;
    }

    // 조직 삭제로 인한 멤버십 해제 (emitToUser로 본인에게만 전송됨)
    if (data.reason === 'organization_deleted') {
      // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
      // 사용자 정보 새로고침 (멤버십 목록 업데이트)
      await fetchUser();

      // 삭제된 조직이 현재 조직인 경우 개인 조직으로 전환
      if (data.companyId === currentCompanyId) {
        const personalMembership = findPersonalOrganization();
        if (personalMembership?.companyId) {
          setCurrentOrganization(personalMembership.companyId);
        }
        navigate('/');
      }
      return;
    }

    // 다른 멤버가 자발적으로 탈퇴한 경우 (각 Owner에게 개별 전송됨)
    if (data.reason === 'left') {
      // 본인이 탈퇴한 경우는 무시 (이미 API 응답으로 처리됨)
      if (data.userId === user?.id) return;

      // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
      // 멤버 목록 캐시 무효화 (소유자 화면 갱신용)
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', data.companyId] });
      // 현장 배정 관련 캐시도 무효화
      queryClient.invalidateQueries({ queryKey: ['sites', data.companyId] });
      queryClient.invalidateQueries({ queryKey: ['siteAssignments'] });
      return;
    }

    // 다른 Owner가 멤버를 제거한 경우 (다른 Owner들에게 전송됨)
    if (data.reason === 'member_removed') {
      // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
      // 멤버 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', data.companyId] });
      queryClient.invalidateQueries({ queryKey: ['sites', data.companyId] });
      queryClient.invalidateQueries({ queryKey: ['siteAssignments'] });
    }
  // authCurrentMembership 제거: currentCompanyId로 충분하고, fetchUser마다 새 객체 생성으로 불필요한 재실행 유발
  }, [currentCompanyId, user, fetchUser, setCurrentOrganization, navigate, findPersonalOrganization, queryClient]);

  // 초대 받음 이벤트 핸들러 (기존 사용자가 초대받았을 때)
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleMemberInvited = useCallback((data: MemberInvitedPayload) => {
    console.log('[WebSocket] MEMBER_INVITED received:', data);
    // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
    // 핸들러 레지스트리 패턴 사용으로 fetchUser()가 React 재렌더링을 트리거해도
    // 소켓 구독이 유지되므로 NOTIFICATION_NEW를 놓치지 않음
    fetchUser();
  }, [fetchUser]);

  // 초대 수락됨 이벤트 핸들러 (초대한 사용자가 수락했을 때 - 모든 Owner에게 전송됨)
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleMemberJoined = useCallback((data: MemberJoinedPayload) => {
    // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
    // 멤버 목록 및 초대 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['organizationMembers', data.companyId] });
    queryClient.invalidateQueries({ queryKey: ['invitations', data.companyId] });
    // 현장별 초대 목록도 무효화 (현장 상세 페이지의 초대 현황)
    queryClient.invalidateQueries({ queryKey: ['siteInvitations'] });
    // 현장 배정 목록도 무효화 (새 멤버가 현장에 배정된 경우)
    queryClient.invalidateQueries({ queryKey: ['siteAssignments'] });
  }, [queryClient]);

  // 역할 변경 이벤트 핸들러 (소유권 이전 등)
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleRoleChanged = useCallback(async (_data: MemberRoleChangedPayload) => {
    // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
    // 사용자 정보 새로고침 (역할 업데이트)
    await fetchUser();
  }, [fetchUser]);

  // 조직 복구 이벤트 핸들러 (관리자가 삭제 예약을 취소한 경우)
  const handleOrganizationRestored = useCallback(async (data: OrganizationRestoredPayload) => {
    const restoredBy = data.restoredBy === 'admin' ? '관리자에 의해 ' : '';
    const badges = { orgName: data.organizationName };
    showToast(`조직이 ${restoredBy}복구되었습니다.`, 'success', undefined, badges);
    addNotification({
      type: 'organization_restored',
      title: '조직 복구',
      message: `'${data.organizationName}' 조직이 ${restoredBy}복구되었습니다.`,
      data: { companyId: data.companyId },
    });

    // 사용자 정보 새로고침 (멤버십 목록 업데이트)
    await fetchUser();
  }, [showToast, fetchUser, addNotification]);

  // 조직 인증 상태 변경 이벤트 핸들러 (admin 승인/반려, cron 자동 전환 등)
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleOrgVerificationStatusChanged = useCallback(
    async (_data: OrganizationVerificationStatusChangedPayload) => {
      // auth store 갱신 — 배너, 설정 페이지 등에서 verificationStatus 반영
      await fetchUser();
    },
    [fetchUser],
  );

  // 현장 배정 이벤트 핸들러
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleSiteMemberAssigned = useCallback((data: SiteMemberAssignedPayload) => {
    console.log('[WebSocket] SITE_MEMBER_ASSIGNED handler called:', data);
    // 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['siteAssignments', data.siteId] });
    queryClient.invalidateQueries({ queryKey: ['site', data.siteId] });
  }, [queryClient]);

  // 현장 배정 해제 이벤트 핸들러
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleSiteMemberUnassigned = useCallback((data: SiteMemberAssignedPayload) => {
    // 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['siteAssignments', data.siteId] });
    queryClient.invalidateQueries({ queryKey: ['site', data.siteId] });
  }, [queryClient]);

  // 정 담당자 지정 필요 이벤트 핸들러 (Owner에게만 전송됨)
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSitePrimaryNeeded = useCallback((_data: SitePrimaryNeededPayload) => {
    // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
    // 캐시 무효화만 수행
  }, []);

  // 현장 정보 수정 이벤트 핸들러 (조직 전체에 브로드캐스트)
  const handleSiteUpdated = useCallback(async (data: SiteUpdatedPayload) => {
    // 본인이 수정한 경우: 무시 (이미 UI 반영됨)
    if (data.updatedByUserId === user?.id) return;

    // Owner이거나 해당 현장의 정 담당자인 경우: 토스트 + 알림 표시
    const currentUser = useAuthStore.getState().user;
    const currentMembership = currentUser?.memberships.find(
      (m) => m.companyId === data.companyId
    );

    // 소유자 여부 확인 — P6-b: 옛 `role === 'OWNER'` → 소유자 **성질**(`isOwner`).
    // 읽는 멤버십이 `currentMembership`이 아니라 **이벤트가 난 조직**(`data.companyId`)의 것이라
    // `usePermissions()`가 답할 수 없다. 이 값은 구독 범위가 아니라 **토스트·알림을 띄울지**만
    // 가른다(아래 캐시 무효화는 소유자 여부와 무관하게 전원 실행).
    const isOwner = currentMembership?.isOwner === true;

    // 본인이 수정한 경우: ACTION_RESULT에서 이미 토스트 표시되므로 브로드캐스트 토스트 생략
    const isSelf = currentUser?.id === data.updatedByUserId;

    // 정 담당자 여부 확인 (API 호출 없이 클라이언트에서 확인 불가)
    // 대신 Owner만 알림 표시하고, 나머지는 조용히 UI 갱신
    if (isOwner && !isSelf) {
      const badges = { siteName: data.siteName };
      showToast(`${data.updatedBy}님이 현장 정보를 수정했습니다.`, 'info', undefined, badges);
      addNotification({
        type: 'site_updated',
        title: '현장 정보 수정',
        message: `${data.updatedBy}님이 '${data.siteName}' 현장 정보를 수정했습니다.`,
        data: { siteId: data.siteId, companyId: data.companyId },
      });
    }

    // 캐시 무효화 - 모든 멤버에 대해 UI 갱신
    queryClient.invalidateQueries({ queryKey: ['sites', data.companyId] });
    queryClient.invalidateQueries({ queryKey: ['site', data.siteId] });
  }, [user, showToast, addNotification, queryClient]);

  // 담당자 유형 변경 이벤트 핸들러
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleSiteAssignmentChanged = useCallback((data: SiteAssignmentChangedPayload) => {
    // 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['siteAssignments', data.siteId] });
  }, [queryClient]);

  // 엔티티 생성·수정·삭제 브로드캐스트(현장·현장 하위 리소스·협력업체·그룹·멤버·현장 결재선)
  // 무효화 표와 삭제 시 이동 판정은 `lib/entityChanged.ts` 한 곳이 소유한다 — 여기서는 그 결과를 실행만.
  // 행위자 본인에게도 도달하지만 무효화는 멱등이라 무해하고, 이동은 판정 함수가 행위자를 거른다.
  const handleEntityChanged = useCallback((data: EntityChangedPayload) => {
    for (const queryKey of resolveEntityQueryKeys(data.entity)) {
      queryClient.invalidateQueries({ queryKey: [...queryKey] });
    }
    // react-query가 아닌 화면(useEffect 직접 조회 — 조직 설정·그룹·멤버·현장 결재선)은 이 버스로 재조회한다.
    bumpEntityVersion(data.entity);
    // 헤더 그룹 셀렉터는 auth store의 `currentMembership.company.groups`를 읽는다 — 그룹 변경은 사용자 재조회로.
    // ⚠️ `fetchUser`가 아니라 `refreshSession`이다. fetchUser는 `isLoading`을 토글하는데,
    // `App.tsx`의 `{isLoading ? 로딩 : <Routes>}` 구조에서 그 토글은 **Routes를 통째로 언마운트**한다
    // (그 화면의 편집 중 폼·열린 모달·타이핑이 날아간다). 이 이벤트는 그룹에 멤버를 하나 넣기만 해도
    // 조직 room 전체에 나가므로, 그 대가를 모든 접속자가 치르게 된다 — 바로 위 `paused`(편집 중
    // 덮어쓰기 방지)가 지키려는 것과 정반대다. `refreshSession`은 같은 `getMe()`를 치되 isLoading을
    // 건드리지 않고 `currentMembership`을 새로 집는다(그 JSDoc이 이 함정을 사유로 적고 있다).
    // 인자는 생략 — 조직을 옮기는 흐름이 아니므로 머물던 조직(`currentCompanyId`)을 그대로 유지한다.
    if (data.entity === 'group') void refreshSession();
    // BrowserRouter라 window.location.pathname이 곧 라우터 경로다(이벤트 시점 값을 읽는다).
    const nav = resolveEntityDeletedNavigation(window.location.pathname, data, user?.id);
    if (nav) {
      showToast(nav.toast, 'info');
      navigate(nav.to, { replace: true });
    }
  }, [queryClient, bumpEntityVersion, refreshSession, user?.id, showToast, navigate]);

  // ============================================
  // 알림 동기화 이벤트 핸들러
  // ============================================

  // 단일 알림 읽음 동기화 (다른 세션에서 읽음 처리됨)
  const handleNotificationRead = useCallback((data: NotificationReadPayload) => {
    syncMarkAsRead(data.notification);
  }, [syncMarkAsRead]);

  // 모든 알림 읽음 동기화 (다른 세션에서 모두 읽음 처리됨)
  const handleNotificationAllRead = useCallback((_data: NotificationAllReadPayload) => {
    syncMarkAllAsRead();
  }, [syncMarkAllAsRead]);

  // 모든 알림 삭제 동기화 (다른 세션에서 삭제됨)
  const handleNotificationCleared = useCallback((_data: NotificationClearedPayload) => {
    syncClearAll();
  }, [syncClearAll]);

  // ============================================
  // 초대 응답 동기화 이벤트 핸들러
  // ============================================

  // 초대 수락/거절 동기화 (본인의 다른 세션에서 응답함)
  const handleInvitationResponded = useCallback(async (_data: InvitationRespondedPayload) => {
    // 사용자 정보 새로고침 (멤버십 및 초대 목록 업데이트)
    await fetchUser();

    // 초대 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] });
    queryClient.invalidateQueries({ queryKey: ['siteInvitations'] });
    queryClient.invalidateQueries({ queryKey: ['invitations'] });
  }, [fetchUser, queryClient]);

  // 초대 취소됨 이벤트 핸들러 (초대가 취소되어 초대받은 사용자에게 알림)
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleInvitationCancelled = useCallback(async (_data: InvitationCancelledPayload) => {
    // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
    // 사용자 정보 새로고침 (초대 목록 업데이트)
    await fetchUser();

    // 초대 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] });
  }, [fetchUser, queryClient]);

  // 초대 거절됨 이벤트 핸들러 (Owner에게 알림)
  // 주의: 서버에서 notificationsService.create()로 DB에 알림을 저장하므로
  // 여기서는 addNotification()을 호출하지 않음 (중복 방지)
  const handleInvitationRejected = useCallback((data: InvitationRejectedPayload) => {
    // 서버에서 DB 알림 생성 → NOTIFICATION_NEW 이벤트로 자동 처리됨
    // 초대 목록 캐시 무효화 (멤버 관리 페이지, 현장 상세 페이지)
    queryClient.invalidateQueries({ queryKey: ['organizationInvitations', data.companyId] });
    queryClient.invalidateQueries({ queryKey: ['siteInvitations'] });
  }, [queryClient]);

  // ============================================
  // 사용자 액션 결과 이벤트 핸들러
  // ============================================

  // 사용자 액션 결과 (본인의 모든 세션에 토스트 및 알림 표시)
  const handleActionResult = useCallback(async (data: ActionResultPayload) => {
    const { message, toastType, action } = data;

    // DEBUG: 이벤트 수신 확인
    console.log('[WebSocket] ACTION_RESULT received:', { action, toastType, message, data: data.data });

    // 본인이 직접 수행한 점검 생성/삭제 액션은 이미 직접 호출 토스트가 표시됨 → 중복 생략
    // (다른 세션 동기화 목적이므로 현재 세션에서는 스킵)
    if (
      (action === 'inspection:created' || action === 'inspection:deleted') &&
      data.userId === user?.id
    ) {
      return;
    }

    // resourceType/resourceId 기반 네비게이션 경로 계산
    const getNavigateTo = (): string | undefined => {
      const resourceType = data.data?.resourceType as string | undefined;
      const resourceId = data.data?.resourceId as string | undefined;
      if (!resourceType || !resourceId) return undefined;

      switch (resourceType) {
        // 단일 리소스
        case 'inspection': return `/inspections/${resourceId}`;
        case 'report': return `/reports/${resourceId}`;
        case 'site': return `/sites/${resourceId}`;
        case 'approval': return `/approvals/${resourceId}`;
        case 'tbm': return `/reports/${resourceId}`;
        case 'organization': return `/settings/organization`;
        case 'profile': return `/settings/profile`;
        // 목록 리소스
        case 'sites': return `/sites`;
        case 'inspections': return `/inspections`;
        case 'members': return `/settings/members`;
        case 'organizations': return `/dashboard`;
        default: return undefined;
      }
    };

    const navigateTo = getNavigateTo();
    const toastAction = navigateTo ? { navigateTo } : undefined;

    // 배지 정보 추출
    const badges = {
      orgName: data.data?.orgName as string | undefined,
      siteName: data.data?.siteName as string | undefined,
    };
    const hasBadges = badges.orgName || badges.siteName;

    // 토스트 표시 (warning은 info로 매핑)
    switch (toastType) {
      case 'success':
        showToast(message, 'success', toastAction, hasBadges ? badges : undefined);
        break;
      case 'error':
        showToast(message, 'error', undefined, hasBadges ? badges : undefined);
        break;
      case 'info':
      case 'warning':
        showToast(message, 'info', toastAction, hasBadges ? badges : undefined);
        break;
    }

    // 알림 목록에는 추가하지 않음 (토스트만 표시)
    // 알림 목록은 서버에서 관리되는 알림만 표시

    // 특정 액션에 대한 추가 처리
    if (action === 'org:left') {
      // 사용자 정보 갱신 (멤버십 목록 업데이트)
      await fetchUser();
      // 탈퇴한 조직이 현재 조직인 경우 다른 조직으로 전환 및 홈으로 이동
      const companyId = data.data?.companyId as string | undefined;
      if (companyId && companyId === currentCompanyId) {
        // 1순위: 개인 조직
        const personalMembership = findPersonalOrganization();
        // 2순위: 탈퇴한 조직을 제외한 첫 번째 활성 멤버십
        const fallbackMembership = useAuthStore.getState().user?.memberships?.find(
          (m) => m.companyId !== companyId && !m.company.deletedAt
        );
        const targetMembership = personalMembership || fallbackMembership;

        if (targetMembership?.companyId) {
          setCurrentOrganization(targetMembership.companyId);
        }
        // 토스트가 확실히 표시되도록 약간의 딜레이 후 네비게이션
        setTimeout(() => navigate('/'), 100);
      }
    }

    // 특정 액션에 대한 캐시 무효화
    if (action.startsWith('org:') || action.startsWith('invitation:')) {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] });
      // 현장 초대 목록도 무효화 (현장 상세 페이지의 초대 현황)
      queryClient.invalidateQueries({ queryKey: ['siteInvitations'] });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    }
    if (action.startsWith('member:')) {
      queryClient.invalidateQueries({ queryKey: ['organizationMembers'] });
    }
    if (action.startsWith('site:')) {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    }
    if (action.startsWith('inspection:')) {
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
    }
    if (action.startsWith('report:')) {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
    // (`['tbms']` queryKey를 쓰는 쿼리가 없어 무효화하지 않는다 — TBM은 문서관리 목록(`['reports']`)의
    //  한 행이라 위 `report:` 갈래가 같은 화면을 맡는다.)
  // authCurrentMembership 제거: 이 핸들러 내에서 직접 사용하지 않음. currentCompanyId로 제어하며 fetchUser마다 새 객체 생성으로 불필요한 재실행 유발
  }, [showToast, addNotification, fetchUser, currentCompanyId, findPersonalOrganization, setCurrentOrganization, queryClient, user]);

  // ============================================
  // 점검 관련 이벤트 핸들러
  // ============================================

  // 사진 분석 시작 이벤트 핸들러 (재분석 대기/분석 중)
  // inspection 캐시 무효화 금지 — 이 시점에 findings는 enrichment 전 중간 상태(법규 없음).
  // 캐시 무효화는 PHOTO_ANALYSIS_COMPLETED에서만 수행 → race 방지.
  const handlePhotoAnalysisStarted = useCallback((_data: { inspectionId: string; photoId: string; analysisStatus: string }) => {
    // intentionally no-op
  }, []);

  // 사진 분석 완료 이벤트 핸들러 (개별 사진 - UI 갱신용)
  // 캐시 무효화를 하지 않음 — 동시 분석(concurrency: 15) 환경에서 사진 1이 완료되면
  // 나머지 사진들의 enrichment가 아직 진행 중인 상태로 refetch되어
  // "법규 없음 → 법규 있음"의 2단계 노출 race가 발생.
  // inspection 캐시 무효화는 모든 사진이 완료된 AI_ANALYSIS_COMPLETED에서만 수행.
  const handlePhotoAnalysisCompleted = useCallback((_data: PhotoAnalysisCompletedPayload) => {
    // intentionally no-op: AI_ANALYSIS_COMPLETED에서 일괄 무효화
  }, []);

  // 사진 분석 실패 이벤트 핸들러 (개별 사진 - UI 갱신용)
  // 캐시 무효화하지 않음 — AI_ANALYSIS_FAILED (점검 단위)에서 일괄 처리.
  const handlePhotoAnalysisFailed = useCallback((_data: PhotoAnalysisFailedPayload) => {
    // intentionally no-op: AI_ANALYSIS_FAILED에서 일괄 무효화
  }, []);

  // ============================================
  // AI 분석 이벤트 핸들러 (점검 단위 - 세션 기반)
  // ============================================

  // AI 분석 시작 이벤트 핸들러
  const handleAIAnalysisStarted = useCallback((data: AIAnalysisStartedPayload) => {
    console.log('[WebSocket] AI_ANALYSIS_STARTED received:', data);

    // _notification이 있으면 토스트 표시 + 알림 목록 추가
    if (data._notification) {
      const { badges, cleanMessage } = parseBadgesFromMessage(data._notification.message);
      const navigateTo = getNavigatePath(data._notification.resourceType, data._notification.resourceId);

      showToast(cleanMessage, 'info', navigateTo ? { navigateTo } : undefined, badges);

      addNotification({
        type: mapServerTypeToLocalType(data._notification.type),
        title: data._notification.title,
        message: data._notification.message,
        data: {
          resourceType: data._notification.resourceType,
          resourceId: data._notification.resourceId,
          serverId: data._notification.id,
        },
      });
    }
  }, [showToast, addNotification]);

  // AI 분석 완료 이벤트 핸들러
  const handleAIAnalysisCompleted = useCallback((data: AIAnalysisCompletedPayload) => {
    console.log('[WebSocket] AI_ANALYSIS_COMPLETED received:', data);

    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    // 점검 목록 새로고침
    triggerInspectionListRefresh();

    // _notification이 있으면 토스트 표시 + 알림 목록 추가
    if (data._notification) {
      const { badges, cleanMessage } = parseBadgesFromMessage(data._notification.message);
      const navigateTo = getNavigatePath(data._notification.resourceType, data._notification.resourceId);

      showToast(cleanMessage, 'success', navigateTo ? { navigateTo } : undefined, badges);

      addNotification({
        type: mapServerTypeToLocalType(data._notification.type),
        title: data._notification.title,
        message: data._notification.message,
        data: {
          resourceType: data._notification.resourceType,
          resourceId: data._notification.resourceId,
          serverId: data._notification.id,
        },
      });
    }
  }, [queryClient, triggerInspectionListRefresh, showToast, addNotification]);

  // AI 분석 실패 이벤트 핸들러
  const handleAIAnalysisFailed = useCallback((data: AIAnalysisFailedPayload) => {
    console.log('[WebSocket] AI_ANALYSIS_FAILED received:', data);

    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    // 점검 목록 새로고침
    triggerInspectionListRefresh();

    // _notification이 있으면 토스트 표시 + 알림 목록 추가
    if (data._notification) {
      const { badges, cleanMessage } = parseBadgesFromMessage(data._notification.message);
      const navigateTo = getNavigatePath(data._notification.resourceType, data._notification.resourceId);

      showToast(cleanMessage, 'error', navigateTo ? { navigateTo } : undefined, badges);

      addNotification({
        type: mapServerTypeToLocalType(data._notification.type),
        title: data._notification.title,
        message: data._notification.message,
        data: {
          resourceType: data._notification.resourceType,
          resourceId: data._notification.resourceId,
          serverId: data._notification.id,
        },
      });
    }
  }, [queryClient, triggerInspectionListRefresh, showToast, addNotification]);

  // 사용량 업데이트 이벤트 핸들러 (실시간 동기화)
  const handleUsageUpdated = useCallback((data: UsageUpdatedPayload) => {
    console.log('[WebSocket] USAGE_UPDATED handler called:', data);
    updateUsageFromSocket(data.type, data.current, data.limit);
  }, [updateUsageFromSocket]);

  // 사진 추가 이벤트 핸들러
  const handlePhotoAdded = useCallback((data: PhotoAddedPayload) => {
    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    // 점검 목록 새로고침 (useState 기반 훅용)
    triggerInspectionListRefresh();
    // 사진 추가는 토스트 표시 안함 (점검 생성 알림으로 충분)
  }, [queryClient, triggerInspectionListRefresh]);

  // 사진 삭제 이벤트 핸들러
  const handlePhotoDeleted = useCallback((data: PhotoDeletedPayload) => {
    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    // 점검 목록 새로고침 (useState 기반 훅용)
    triggerInspectionListRefresh();
    // 사진 삭제는 토스트 표시 안함
  }, [queryClient, triggerInspectionListRefresh]);

  // 사진 일괄 삭제 이벤트 핸들러
  const handlePhotosDeleted = useCallback((data: PhotosDeletedPayload) => {
    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    // 점검 목록 새로고침 (useState 기반 훅용)
    triggerInspectionListRefresh();
    // 일괄 삭제도 토스트 표시 안함
  }, [queryClient, triggerInspectionListRefresh]);

  // 사진 검토 상태 변경 이벤트 핸들러
  const handlePhotoReviewUpdated = useCallback((data: PhotoReviewUpdatedPayload) => {
    // 본인이 변경한 경우는 무시 (이미 UI에서 처리됨)
    if (data.reviewedBy === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      return;
    }

    // 번호 + 카테고리 라벨 조합 (예: "#2 떨어짐 > 고소작업")
    const indexPrefix = data.photoIndex ? `#${data.photoIndex} ` : '';
    const itemLabel = data.categoryLabel
      ? `${indexPrefix}${data.categoryLabel}`
      : `${indexPrefix}항목`.trim();
    const statusText = data.isReviewed ? '검토가 완료' : '검토가 취소';
    showToast(`${itemLabel}의 ${statusText}되었습니다.`, 'info');

    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
  }, [user?.id, showToast, queryClient]);

  // 사진 일괄 검토 완료 이벤트 핸들러
  const handlePhotosBulkReviewed = useCallback((data: PhotosBulkReviewedPayload) => {
    // 본인이 변경한 경우는 무시 (이미 UI에서 처리됨)
    if (data.reviewedBy === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      return;
    }

    showToast(`${data.updatedCount}개 사진이 일괄 검토 완료되었습니다.`, 'info');

    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
  }, [user?.id, showToast, queryClient]);

  // 발견사항 추가 이벤트 핸들러
  const handleFindingAdded = useCallback((data: FindingAddedPayload) => {
    const rediscoveredText = data.isRediscovered ? ' (재발견)' : '';
    const badges = data.site?.name ? { siteName: data.site.name } : undefined;
    showToast(`발견사항이 추가되었습니다${rediscoveredText}`, 'info', undefined, badges);

    // 점검 상세 및 목록 캐시 무효화 (발견 건수 업데이트)
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
  }, [showToast, queryClient]);

  // 발견사항 수정 이벤트 핸들러
  const handleFindingUpdated = useCallback((data: FindingUpdatedPayload) => {
    // 본인이 수정한 경우는 무시 (이미 UI에서 처리됨)
    if (data.updatedBy?.id === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['previousFindings', data.inspectionId] });
      return;
    }

    // (`lawSearchStatus` 분기는 없다 — 서버 `FINDING_UPDATED` emit 3자리(`inspections.controller.ts`
    //  발견사항 수정·미해결 되돌리기·조치결과) 어디도 그 키를 싣지 않는다. 법령 검색 진행 표시는
    //  REST `finding.lawSearchStatus`를 `FindingDetail`이 읽는 것이라 WS와 무관. 2026-09-18 R14 삭제)

    // 조치결과 입력 이벤트인 경우 별도 처리
    if (data.isActionResult) {
      const updaterName = data.updatedBy?.name || '다른 사용자';
      const photoText = data.hasPhoto ? ' (사진 첨부)' : '';
      showToast(`${updaterName}님이 이전 발견사항 조치결과를 입력했습니다${photoText}`, 'info');

      // 점검 상세 및 이전 미해결 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['previousFindings', data.inspectionId] });
      return;
    }

    // 수정된 필드 한글 변환
    const fieldLabels: Record<string, string> = {
      'category': '카테고리',
      'subCategory': '서브카테고리',
      'description': '설명',
      'severity': '심각도',
      'location': '위치',
      'riskAssessment': '위험성 평가',
      'riskAssessment.likelihood': '위험성 평가 빈도',
      'riskAssessment.severity': '위험성 평가 강도',
      'riskAssessment.riskLevel': '위험성 평가 위험도',
      'measures': '개선대책',
      'legalBasis': '관련법규',
      'boundingBox': '위험영역',
    };

    // 수정된 필드 목록 생성
    const updatedFieldsText = data.updatedFields?.length
      ? data.updatedFields.map(f => fieldLabels[f] || f).join(', ')
      : '발견사항';

    // 현장 정보가 있으면 배지로 표시
    const badges = data.site?.name ? { siteName: data.site.name } : undefined;
    const updaterName = data.updatedBy?.name || '다른 사용자';

    // 되돌리기 여부에 따라 다른 메시지 표시
    const actionText = data.isRevert
      ? `수정을 취소했습니다 (${updatedFieldsText})`
      : `점검 자료를 수정했습니다 (${updatedFieldsText})`;

    showToast(`${updaterName}님이 ${actionText}`, 'info', undefined, badges);

    // 변경된 발견사항에 하이라이트 추가
    if (data.finding?.id && data.updatedFields) {
      addHighlight(data.finding.id, data.updatedFields, data.updatedBy?.name);
    }

    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
  }, [user, showToast, queryClient, addHighlight]);

  // 발견사항 해결 이벤트 핸들러
  const handleFindingResolved = useCallback((data: FindingResolvedPayload) => {
    // 본인이 해결한 경우는 무시 (이미 UI에서 처리됨)
    if (data.updatedBy?.id === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['previousFindings', data.inspectionId] });
      return;
    }

    const resolverName = data.resolverName || data.updatedBy?.name || '다른 사용자';
    const evidenceText = data.hasEvidence ? ' (증빙 사진 첨부)' : '';
    showToast(`${resolverName}님이 이전 발견사항을 해결 처리했습니다${evidenceText}`, 'success');

    // 점검 상세 및 이전 미해결 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['previousFindings', data.inspectionId] });
  }, [user, showToast, queryClient]);

  // 발견사항 삭제 이벤트 핸들러
  const handleFindingDeleted = useCallback((data: FindingDeletedPayload) => {
    const badges = data.site?.name ? { siteName: data.site.name } : undefined;
    showToast('발견사항이 삭제되었습니다', 'info', undefined, badges);

    // 점검 상세 및 목록 캐시 무효화 (발견 건수 업데이트)
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
  }, [showToast, queryClient]);

  // 발견사항 복원 이벤트 핸들러
  const handleFindingRestored = useCallback((data: FindingRestoredPayload) => {
    // 본인이 복원한 경우는 무시 (이미 UI에서 처리됨)
    if (data.restoredBy?.id === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      return;
    }

    const badges = data.site?.name ? { siteName: data.site.name } : undefined;
    const restorerName = data.restoredBy?.name || '다른 사용자';
    showToast(`${restorerName}님이 삭제된 발견사항을 복원했습니다`, 'info', undefined, badges);

    // 점검 상세 및 목록 캐시 무효화 (발견 건수 업데이트)
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
  }, [user, showToast, queryClient]);

  // 개선대책 추가 이벤트 핸들러
  const handleFindingMeasureAdded = useCallback((data: FindingMeasureAddedPayload) => {
    // 본인이 추가한 경우는 무시 (이미 UI에서 처리됨)
    if (data.updatedBy?.id === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      return;
    }

    const badges = data.site?.name ? { siteName: data.site.name } : undefined;
    const updaterName = data.updatedBy?.name || '다른 사용자';
    showToast(`${updaterName}님이 개선대책을 추가했습니다`, 'info', undefined, badges);

    // 변경된 발견사항에 하이라이트 추가
    if (data.findingId) {
      addHighlight(data.findingId, ['measures'], data.updatedBy?.name);
    }

    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
  }, [user, showToast, queryClient, addHighlight]);

  // 개선대책 삭제 이벤트 핸들러
  const handleFindingMeasureRemoved = useCallback((data: FindingMeasureRemovedPayload) => {
    // 본인이 삭제한 경우는 무시 (이미 UI에서 처리됨)
    if (data.updatedBy?.id === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      return;
    }

    const badges = data.site?.name ? { siteName: data.site.name } : undefined;
    const updaterName = data.updatedBy?.name || '다른 사용자';
    showToast(`${updaterName}님이 개선대책을 삭제했습니다`, 'info', undefined, badges);

    // 변경된 발견사항에 하이라이트 추가
    if (data.findingId) {
      addHighlight(data.findingId, ['measures'], data.updatedBy?.name);
    }

    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
  }, [user, showToast, queryClient, addHighlight]);

  // 관련법규 추가 이벤트 핸들러
  const handleFindingLegalAdded = useCallback((data: FindingLegalAddedPayload) => {
    // 본인이 추가한 경우는 무시 (이미 UI에서 처리됨)
    if (data.updatedBy?.id === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      return;
    }

    const badges = data.site?.name ? { siteName: data.site.name } : undefined;
    const updaterName = data.updatedBy?.name || '다른 사용자';
    showToast(`${updaterName}님이 관련법규를 추가했습니다`, 'info', undefined, badges);

    // 변경된 발견사항에 하이라이트 추가
    if (data.findingId) {
      addHighlight(data.findingId, ['legalBasis'], data.updatedBy?.name);
    }

    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
  }, [user, showToast, queryClient, addHighlight]);

  // 관련법규 삭제 이벤트 핸들러
  const handleFindingLegalRemoved = useCallback((data: FindingLegalRemovedPayload) => {
    // 본인이 삭제한 경우는 무시 (이미 UI에서 처리됨)
    if (data.updatedBy?.id === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      return;
    }

    const badges = data.site?.name ? { siteName: data.site.name } : undefined;
    const updaterName = data.updatedBy?.name || '다른 사용자';
    showToast(`${updaterName}님이 관련법규를 삭제했습니다`, 'info', undefined, badges);

    // 변경된 발견사항에 하이라이트 추가
    if (data.findingId) {
      addHighlight(data.findingId, ['legalBasis'], data.updatedBy?.name);
    }

    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
  }, [user, showToast, queryClient, addHighlight]);

  // 검수 완료 이벤트 핸들러
  const handleInspectionReviewCompleted = useCallback((data: InspectionReviewCompletedPayload) => {
    if (data.allReviewsCompleted) {
      showToast('모든 검수가 완료되었습니다. 보고서 생성이 가능합니다.', 'success');
    } else {
      showToast(`${data.reviewerName}님이 검수를 완료했습니다.`, 'info');
    }

    // 점검 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
  }, [showToast, queryClient]);

  // 점검 캐시 무효화 헬퍼
  //
  // ⚠️ `['todayScheduledInspections']`의 **WS 경로는 여기 하나뿐이다**(2026-09-29 신설).
  //    종전엔 `Layout`이 같은 이벤트(`INSPECTION_STATUS_CHANGED`)를 `registerHandler`로 잡아
  //    `setQueryData`로 시작된 점검을 그 목록에서 뺐다. 그런데 그 처리는 **죽어 있었다** —
  //    `registerHandler`는 이벤트당 핸들러 **하나를 교체**하고(`stores/websocket.ts`의
  //    `_eventHandlers.set`), 이 Provider가 `Layout`을 감싸므로(`App.tsx`) effect가 자식 먼저
  //    도는 초기 마운트에서 **Provider가 나중에 등록해 이겼다**. 즉 그 목록의 실시간 반영은
  //    한 번도 WS로 돈 적이 없고, 로컬 행위 경로 3곳의 invalidate만 덮고 있었다.
  //    배너를 대시보드 위젯으로 옮기며(줄기 `banner-slot-policy` P5) 경쟁을 없애고 무효화를
  //    이 한 곳으로 모은다 — **위젯은 이 키를 다시 등록하지 않는다**(등록하면 같은 교체 규약
  //    때문에 아래 토스트·무효화를 덮는다). 가드:
  //    `__tests__/inspectionInvalidationOwnership.test.ts`.
  const invalidateInspectionCaches = useCallback((inspectionId?: string) => {
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
    queryClient.invalidateQueries({ queryKey: ['inspectionSchedules'] });
    // 루트 헬퍼를 쓴다 — 리터럴을 적으면 키 문자열을 고칠 때 **조용히** 아무것도 무효화하지
    // 않는다(2026-10-05). 전체 키(`todayScheduledQueryKey`)가 아닌 이유는 회사를 가리지 않고
    // 전부 무효화해야 하기 때문이다(조직 전환·빙의).
    queryClient.invalidateQueries({ queryKey: todayScheduledQueryRootKey() });
    if (inspectionId) {
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    }
    triggerInspectionListRefresh();
  }, [queryClient, triggerInspectionListRefresh]);

  // 점검 삭제 이벤트 핸들러
  const handleInspectionDeleted = useCallback((data: InspectionDeletedPayload) => {
    // 캐시 무효화 (본인 포함 — 다른 창 갱신)
    invalidateInspectionCaches(data.inspectionId);

    // 본인이 삭제한 경우 토스트 스킵
    if (data.deletedBy === user?.id) return;

    const badges = { siteName: data.siteName };
    showToast(`${data.deletedByName}님이 점검을 삭제했습니다.`, 'info', undefined, badges);
  }, [user, showToast, invalidateInspectionCaches]);

  // 점검 생성 이벤트 핸들러
  const handleInspectionCreated = useCallback((data: InspectionCreatedPayload) => {
    // 캐시 무효화 (본인 포함 — 다른 창 갱신)
    invalidateInspectionCaches();

    // 본인이 생성한 경우 토스트 스킵
    if (data.inspectorId === user?.id) return;

    const isScheduled = data.status === 'SCHEDULED';
    const actionText = isScheduled ? '예약' : '시작';
    const badges = { siteName: data.siteName };
    showToast(`${data.inspectorName}님이 점검을 ${actionText}했습니다.`, 'info', {
      navigateTo: `/inspections/${data.inspectionId}`,
    }, badges);
    addNotification({
      type: 'inspection_created',
      title: isScheduled ? '새 점검 예약' : '새 점검 시작',
      message: `${data.inspectorName}님이 '${data.siteName}' 현장에서 점검을 ${actionText}했습니다.`,
      data: { inspectionId: data.inspectionId, siteId: data.siteId },
    });
  }, [user, showToast, addNotification, invalidateInspectionCaches]);

  // 점검 상태 변경 이벤트 핸들러
  const handleInspectionStatusChanged = useCallback((data: InspectionStatusChangedPayload) => {
    // 점검 캐시 무효화
    invalidateInspectionCaches(data.inspectionId);

    // SCHEDULED → IN_PROGRESS: 점검 시작 토스트
    if (data.oldStatus === 'SCHEDULED' && data.newStatus === 'IN_PROGRESS') {
      const isSelf = data.changedBy === user?.id;
      const badges = { siteName: data.siteName };
      showToast(
        isSelf ? '점검이 시작됐습니다.' : `${data.changedByName}님이 점검을 시작했습니다.`,
        'info',
        { navigateTo: `/inspections/${data.inspectionId}` },
        badges,
      );
    }
  }, [invalidateInspectionCaches, user, showToast]);

  // 점검 업데이트 이벤트 핸들러
  const handleInspectionUpdated = useCallback((data: InspectionUpdatedPayload) => {
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    triggerInspectionListRefresh();
  }, [queryClient, triggerInspectionListRefresh]);

  // ============================================
  // 공동 점검 관련 이벤트 핸들러
  // ============================================

  // 기여자 Room 합류 이벤트 핸들러 (공동 점검 Room)
  // `siteName`은 서버 `InspectionCollaboratorService.joinInspection`이 싣는다(2026-09-18 R12).
  // 빈 문자열(site 행 부재)이면 현장명 없는 문구로 떨어진다 — `undefined`를 문구에 섞지 않는다.
  const handleContributorJoinedRoom = useCallback((data: InspectionContributorJoinedRoomPayload) => {
    // 본인이 참여한 경우는 무시 (이미 UI에서 처리됨)
    if (data.contributor.userId === user?.id) return;

    // 토스트는 현장명을 배지로(다른 점검 이벤트와 같은 규약), 알림 목록은 문구에 넣는다
    const badges = data.siteName ? { siteName: data.siteName } : undefined;
    showToast(`${data.contributor.userName}님이 점검에 참여했습니다.`, 'info', undefined, badges);
    addNotification({
      type: 'inspection_contributor_joined_room',
      title: '점검 참여자',
      message: data.siteName
        ? `${data.contributor.userName}님이 '${data.siteName}' 점검에 참여했습니다.`
        : `${data.contributor.userName}님이 점검에 참여했습니다.`,
      data: { inspectionId: data.inspectionId, contributorId: data.contributor.id },
    });

    // 점검 상세 및 기여자 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId, 'contributors'] });
    queryClient.invalidateQueries({ queryKey: ['inspections', 'in-progress'] });
  }, [user, showToast, addNotification, queryClient]);

  // 참여자 퇴장 이벤트 핸들러
  // ⚠️ 퇴장 페이로드는 **평면**이다(`{inspectionId, userId, userName, leftAt}`) — 합류와 달리
  //    `contributor` 중첩도 `siteName`도 없다. 2026-09-18 이전에는 web 로컬 타입이 중첩 구조로
  //    선언돼 있어 `data.contributor.userId`에서 TypeError가 나 퇴장 토스트가 아예 안 떴다.
  const handleContributorLeft = useCallback((data: InspectionContributorLeftRoomPayload) => {
    // 본인이 퇴장한 경우는 무시 (이미 UI에서 처리됨)
    if (data.userId === user?.id) return;

    showToast(`${data.userName}님이 점검에서 나갔습니다.`, 'info');

    // 점검 상세 및 참여자 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId, 'contributors'] });
    queryClient.invalidateQueries({ queryKey: ['inspections', 'in-progress'] });
  }, [user, showToast, queryClient]);

  // 참여자 검수 완료 이벤트 핸들러
  // 점검완료 서명·개별 검수 완료 두 업무가 같은 이벤트를 **중첩 형상 한 벌**로 쏜다(서버
  // `buildContributorReviewCompletedPayload`, 2026-09-18 R13). 평면 키(`reviewedBy` 등)는 오지 않는다.
  const handleContributorReviewCompleted = useCallback((data: InspectionContributorReviewCompletedPayload) => {
    const reviewerUserId = data.contributor.userId;
    const reviewerName = data.contributor.userName;
    const allReviewed = data.allContributorsReviewed;

    // 중복 이벤트 체크
    if (isDuplicateEvent('CONTRIBUTOR_REVIEW_COMPLETED', `${data.inspectionId}-${reviewerUserId}`)) return;

    // 본인이 완료한 경우: 토스트/알림은 생략하지만 캐시 무효화는 수행 (아래 공통 처리)
    if (reviewerUserId === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId, 'contributors'] });
      return;
    }

    if (allReviewed) {
      showToast('모든 참여자가 검수를 완료했습니다. 점검을 종료할 수 있습니다.', 'success');
      addNotification({
        type: 'inspection_all_reviews_done',
        title: '검수 완료',
        message: '모든 참여자가 검수를 완료했습니다. 점검을 종료할 수 있습니다.',
        data: { inspectionId: data.inspectionId },
      });
    } else {
      showToast(`${reviewerName}님이 검수를 완료했습니다.`, 'info');
    }

    // 점검 상세 및 참여자 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId, 'contributors'] });
    // 스케줄 페이지 실시간 반영
    queryClient.invalidateQueries({ queryKey: ['inspectionSchedules'] });
  }, [user, showToast, addNotification, queryClient, isDuplicateEvent]);

  // 참여자 점검완료 취소 이벤트 핸들러
  const handleContributorReviewCancelled = useCallback((data: InspectionContributorReviewCancelledPayload) => {
    // 중복 이벤트 체크
    if (isDuplicateEvent('CONTRIBUTOR_REVIEW_CANCELLED', `${data.inspectionId}-${data.contributor.userId}`)) return;

    // 본인이 취소한 경우: 토스트/알림은 생략하지만 캐시 무효화는 수행 (아래 공통 처리)
    if (data.contributor.userId === user?.id) {
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId, 'contributors'] });
      return;
    }

    const badges = { siteName: data.siteName };
    showToast(`${data.contributor.userName}님이 점검완료를 취소했습니다.`, 'info', undefined, badges);

    // 점검 상세 및 참여자 목록 캐시 무효화 → allCompleted 재계산 → 보고서 생성 버튼 비활성
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId, 'contributors'] });
    // 스케줄 페이지 실시간 반영
    queryClient.invalidateQueries({ queryKey: ['inspectionSchedules'] });
  }, [user, showToast, queryClient, isDuplicateEvent]);

  // 모든 참여자 점검완료 서명 완료 이벤트 (inspection room 브로드캐스트)
  const handleInspectionAllCompleted = useCallback((data: InspectionAllCompletedPayload) => {
    // 본인이 마지막 완료자인 경우는 이미 UI에서 처리됨
    if (data.completedBy === user?.id) return;

    // 점검 상세 캐시 무효화 → 보고서 생성 버튼 활성화 조건 재계산
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
  }, [user, queryClient]);

  // ============================================
  // 점검 담당자 알림 이벤트 핸들러
  // ============================================

  // 점검 자료 수정 알림 핸들러 (점검 담당자에게 직접 전송됨)
  const handleInspectionModified = useCallback((data: InspectionModifiedPayload) => {
    // 수정 유형별 한글 변환
    const modifyTypeLabels: Record<string, string> = {
      finding_updated: '발견사항 수정',
      finding_added: '발견사항 추가',
      finding_deleted: '발견사항 삭제',
      finding_restored: '발견사항 복원',
      photo_deleted: '사진 삭제',
      measure_added: '개선대책 추가',
      measure_removed: '개선대책 삭제',
      legal_added: '관련법규 추가',
      legal_removed: '관련법규 삭제',
    };

    const modifyTypeText = modifyTypeLabels[data.modifyType] || '수정';
    const dateText = new Date(data.inspectionDate).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });

    const badges = { siteName: data.siteName };
    const toastMessage = `${data.modifiedBy.name}님이 ${dateText} 점검의 ${modifyTypeText}을 수행했습니다.`;
    const notificationMessage = `${data.modifiedBy.name}님이 '${data.siteName}' ${dateText} 점검의 ${modifyTypeText}을 수행했습니다.`;

    showToast(toastMessage, 'info', undefined, badges);
    addNotification({
      type: 'inspection_modified',
      title: '점검 자료 수정',
      message: notificationMessage,
      data: {
        inspectionId: data.inspectionId,
        companyId: data.companyId,
        siteId: data.siteId,
        modifyType: data.modifyType,
      },
    });

    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
  }, [showToast, addNotification, queryClient]);

  // 새 기여자 합류 알림 핸들러 (점검 담당자에게 직접 전송됨)
  const handleContributorJoinedNotify = useCallback((data: InspectionContributorJoinedPayload) => {
    const dateText = new Date(data.inspectionDate).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });

    const badges = { siteName: data.siteName };
    const toastMessage = `${data.contributor.name}님이 ${dateText} 점검에 참여했습니다.`;
    const notificationMessage = `${data.contributor.name}님이 '${data.siteName}' ${dateText} 점검에 참여했습니다.`;

    showToast(toastMessage, 'info', undefined, badges);
    addNotification({
      type: 'inspection_contributor_joined',
      title: '점검 참여자',
      message: notificationMessage,
      data: {
        inspectionId: data.inspectionId,
        companyId: data.companyId,
        siteId: data.siteId,
        contributorId: data.contributor.id,
      },
    });

    // 점검 상세 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspections', 'in-progress'] });
  }, [showToast, addNotification, queryClient]);

  // ============================================
  // 점검 참여자 알림 핸들러 (참여자에게 직접 전송됨)
  // ============================================

  // 점검 삭제됨 알림 핸들러
  const handleInspectionSoftDeleted = useCallback((data: InspectionSoftDeletedPayload) => {
    const dateText = new Date(data.inspectionDate).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });

    const badges = { siteName: data.siteName };
    const toastMessage = `${data.deletedBy.name}님이 ${dateText} 점검을 삭제했습니다.`;
    const notificationMessage = `${data.deletedBy.name}님이 '${data.siteName}' ${dateText} 점검을 삭제했습니다.`;

    showToast(toastMessage, 'info', undefined, badges);
    addNotification({
      type: 'inspection_soft_deleted',
      title: '점검 삭제됨',
      message: notificationMessage,
      data: {
        inspectionId: data.inspectionId,
        companyId: data.companyId,
        siteId: data.siteId,
      },
    });

    // 점검 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
  }, [showToast, addNotification, queryClient]);

  // 점검 복원됨 알림 핸들러
  const handleInspectionRestored = useCallback((data: InspectionRestoredPayload) => {
    const dateText = new Date(data.inspectionDate).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });

    const isMyAction = data.restoredBy.id === user?.id;

    // 본인이 복원한 경우 토스트 생략 (페이지에서 이미 성공 토스트 표시됨)
    if (!isMyAction) {
      const badges = { siteName: data.siteName };
      const toastMessage = `${data.restoredBy.name}님이 ${dateText} 점검을 복원했습니다.`;
      const notificationMessage = `${data.restoredBy.name}님이 '${data.siteName}' ${dateText} 점검을 복원했습니다.`;

      showToast(toastMessage, 'success', undefined, badges);
      addNotification({
        type: 'inspection_restored',
        title: '점검 복원됨',
        message: notificationMessage,
        data: {
          inspectionId: data.inspectionId,
          companyId: data.companyId,
          siteId: data.siteId,
        },
      });
    }

    // 점검 목록 캐시 무효화 (본인 여부와 상관없이 항상 수행)
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
    // 점검 목록 새로고침 트리거 (useState 기반 훅용)
    triggerInspectionListRefresh();
  }, [user, showToast, addNotification, queryClient, triggerInspectionListRefresh]);

  // 점검 완료됨 알림 핸들러
  const handleInspectionCompletedNotify = useCallback((data: InspectionCompletedNotifyPayload) => {
    const isMyAction = data.completedBy.id === user?.id;

    const dateText = new Date(data.inspectionDate).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });
    const badges = { siteName: data.siteName };

    if (isMyAction) {
      // 본인 액션: 간단한 토스트만 표시 (알림 목록은 NOTIFICATION_NEW에서 추가됨)
      showToast(`${dateText} 점검이 완료되었습니다.`, 'success', undefined, badges);
    } else {
      // 다른 사람 액션: 누가 완료했는지 표시 + 알림 목록 추가
      const toastMessage = `${data.completedBy.name}님이 ${dateText} 점검을 완료 처리했습니다.`;
      const notificationMessage = `${data.completedBy.name}님이 '${data.siteName}' ${dateText} 점검을 완료 처리했습니다.`;

      showToast(toastMessage, 'info', undefined, badges);
      addNotification({
        type: 'inspection_completed',
        title: '점검 완료',
        message: notificationMessage,
        data: {
          inspectionId: data.inspectionId,
          companyId: data.companyId,
          siteId: data.siteId,
        },
      });
    }

    // 점검 상세/목록 캐시 무효화 (본인 여부와 상관없이 항상 수행)
    queryClient.invalidateQueries({ queryKey: ['inspection', data.inspectionId] });
    queryClient.invalidateQueries({ queryKey: ['inspections'] });
  }, [user, showToast, addNotification, queryClient]);

  // 보고서 생성됨 알림 핸들러
  const handleReportCreatedNotify = useCallback((data: ReportCreatedNotifyPayload) => {
    // 중복 이벤트 체크 (emitToUser + emitToInspection + emitToOrganization으로 여러 번 수신 가능)
    if (isDuplicateEvent('REPORT_CREATED', data.reportId)) return;

    const dateText = new Date(data.inspectionDate).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });

    const isMyAction = data.createdBy.id === user?.id;

    // 본인이 생성한 경우 토스트 생략 (write.tsx에서 이미 성공 토스트 표시됨)
    if (!isMyAction) {
      const notificationMessage = `${data.createdBy.name}님이 '${data.siteName}' ${dateText} 점검에 대한 보고서를 생성했습니다. 검토 후 서명해 주세요.`;
      showToast(
        notificationMessage,
        'info',
        { navigateTo: `/reports/${data.reportId}` },
        { orgName: data.organizationName, siteName: data.siteName }
      );
    }

    // 알림 목록은 NOTIFICATION_NEW 이벤트 또는 fetchFromServer에서 추가됨
    // (serverId가 포함되어야 새로고침 시에도 유지됨)

    // 보고서 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  }, [user, queryClient, showToast, isDuplicateEvent]);

  // 보고서 생성됨 UI 업데이트 핸들러 (토스트 없음, 목록 새로고침용)
  const handleReportCreated = useCallback((_data: ReportCreatedNotifyPayload) => {
    // 단순히 캐시 무효화만 (토스트와 알림은 REPORT_CREATED_NOTIFY에서 처리)
    queryClient.invalidateQueries({ queryKey: ['reports'] });
    // 해당 점검 + 이전 점검도 무효화 (nextInspectionHasReport 갱신)
    queryClient.invalidateQueries({ queryKey: ['inspection'] });
  }, [queryClient]);

  // 보고서 삭제됨 UI 업데이트 핸들러 (토스트 없음, 목록 새로고침용)
  const handleReportDeleted = useCallback((_data: ReportDeletedNotifyPayload) => {
    // 단순히 캐시 무효화만 (토스트와 알림은 REPORT_DELETED_NOTIFY에서 처리)
    queryClient.invalidateQueries({ queryKey: ['reports'] });
    // 해당 점검 + 이전 점검도 무효화 (nextInspectionHasReport 갱신)
    queryClient.invalidateQueries({ queryKey: ['inspection'] });
  }, [queryClient]);

  // 보고서 삭제됨 알림 핸들러
  const handleReportDeletedNotify = useCallback((data: ReportDeletedNotifyPayload) => {
    // 중복 이벤트 체크 (emitToUser + emitToInspection + emitToOrganization으로 여러 번 수신 가능)
    if (isDuplicateEvent('REPORT_DELETED', data.reportId)) return;

    const dateText = new Date(data.inspectionDate).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });

    const isMyAction = data.deletedBy.id === user?.id;

    // 본인이 삭제한 경우 토스트 생략 (페이지에서 이미 성공 토스트 표시됨)
    if (!isMyAction) {
      const notificationMessage = `${data.deletedBy.name}님이 '${data.siteName}' ${dateText} 점검의 보고서를 삭제했습니다.`;
      showToast(
        notificationMessage,
        'info',
        { navigateTo: `/inspections/${data.inspectionId}` },
        { orgName: data.organizationName, siteName: data.siteName }
      );
    }

    // 알림 목록은 NOTIFICATION_NEW 이벤트 또는 fetchFromServer에서 추가됨
    // (serverId가 포함되어야 새로고침 시에도 유지됨)

    // 보고서 목록 캐시 무효화
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  }, [user, queryClient, showToast, isDuplicateEvent]);

  // ============================================
  // 실시간 알림 이벤트 핸들러 (서버에서 전송된 DB 알림)
  // ============================================

  interface NotificationNewPayload {
    id: string;
    type: string;
    title: string;
    message: string;
    resourceType?: string;
    resourceId?: string;
    actionUrl?: string;
    createdAt: string;
  }

  // 리소스 타입에 따른 이동 경로 결정
  // `NotificationMetadata.resourceType/resourceId`는 nullable 컬럼에서 오므로 `null`도 받는다.
  const getNavigatePath = (resourceType?: string | null, resourceId?: string | null): string | undefined => {
    if (!resourceType || !resourceId) return undefined;

    const pathMap: Record<string, string> = {
      report: `/reports/${resourceId}`,
      inspection: `/inspections/${resourceId}`,
      site: `/sites/${resourceId}`,
      organization: `/organization`,
      // 법정 서류 기한 임박 — 헤더 벨·알림 목록과 같은 경로 함수(`lib/legalDocumentRoutes.ts`)
      [LEGAL_OBLIGATION_INSTANCE_RESOURCE_TYPE]: legalObligationInstancePath(resourceId),
      // 사건 등록 취소 — resourceId = 현장 id
      [LEGAL_OBLIGATION_SITE_RESOURCE_TYPE]: legalSiteObligationsPath(resourceId),
      // 대행 연결 요청·처리(P4) — 헤더 벨·알림 목록과 같은 경로 함수(`lib/agencySiteLinkView.ts`)
      [AGENCY_LINK_REQUEST_RESOURCE_TYPE]: agencyLinkRequestPath(resourceId),
    };

    return pathMap[resourceType];
  };

  /**
   * 메시지에서 배지 정보 파싱
   * 패턴: "[조직명][현장명] 메시지" 또는 "[조직명] 메시지"
   * @returns { badges, cleanMessage }
   */
  const parseBadgesFromMessage = (message: string): {
    badges?: { orgName?: string; siteName?: string };
    cleanMessage: string;
  } => {
    // 패턴: [조직명][현장명] 또는 [조직명]
    const regex = /^\[([^\]]+)\](?:\[([^\]]+)\])?\s*/;
    const match = message.match(regex);

    if (!match) {
      return { cleanMessage: message };
    }

    const orgName = match[1];
    const siteName = match[2]; // undefined if not present
    const cleanMessage = message.replace(regex, '').trim();

    return {
      badges: { orgName, siteName: siteName || undefined },
      cleanMessage,
    };
  };

  // 실시간 알림 이벤트 핸들러
  const handleNotificationNew = useCallback((data: NotificationNewPayload) => {
    console.log('[WebSocket] NOTIFICATION_NEW received:', data);

    // 별도 핸들러가 토스트를 표시하는 타입은 여기서 토스트 생략 (중복 방지)
    // 이 타입들은 *_NOTIFY 이벤트 핸들러에서 토스트를 직접 표시함
    const typesWithDedicatedHandler = [
      'INSPECTION_COMPLETED', // handleInspectionCompletedNotify에서 처리
      'REPORT_CREATED',
      'REPORT_DELETED',
    ];
    const skipToast = typesWithDedicatedHandler.includes(data.type);

    if (!skipToast) {
      // 조직 통합 알림 셋은 resourceType(company_merge_request)·resourceId(토큰)가 모두 같아
      // 목적지가 알림 타입으로만 갈린다. 수락 화면은 요청을 *받은* 조직만 열 수 있고(그 외 403),
      // 수락/거절 통지는 요청한 쪽이 받으므로 진행 상태를 보는 조직 설정으로 보낸다.
      const mergeNavigateTo =
        data.type === 'COMPANY_MERGE_REQUEST' && data.resourceId
          ? `/company-merge/${data.resourceId}`
          : data.type === 'COMPANY_MERGE_ACCEPTED' || data.type === 'COMPANY_MERGE_REJECTED'
            ? '/settings/organization'
            : undefined;
      const navigateTo =
        data.actionUrl ?? mergeNavigateTo ?? getNavigatePath(data.resourceType, data.resourceId);

      // 메시지에서 배지 정보 파싱 ([조직명][현장명] 형태)
      const { badges, cleanMessage } = parseBadgesFromMessage(data.message);

      // 토스트 표시 (배지 + 클린 메시지)
      showToast(
        cleanMessage,
        'info',
        navigateTo ? { navigateTo } : undefined,
        badges
      );
    }

    // 알림 목록에 추가 (서버 ID 포함하여 중복 방지 및 동기화)
    addNotification({
      type: mapServerTypeToLocalType(data.type),
      title: data.title,
      message: data.message, // 원본 메시지 저장 (알림 목록에서는 전체 표시)
      data: {
        resourceType: data.resourceType,
        resourceId: data.resourceId,
        serverId: data.id, // 서버 알림 ID 저장 (fetchFromServer 시 중복 방지)
      },
    });
  }, [showToast, addNotification]);

  // 이전 점검 상태 변경 이벤트 핸들러 (같은 현장의 다른 점검에서 이전 발견사항 갱신)
  const handlePreviousInspectionCompleted = useCallback((data: PreviousInspectionCompletedPayload) => {
    console.log('[WebSocket] PREVIOUS_INSPECTION_COMPLETED received:', data);

    // 이전 발견사항 캐시를 무효화하여 같은 현장의 점검들이 갱신된 이전 발견사항을 로드하도록 함
    // 쿼리 키가 ['previousFindings', inspectionId]이므로 해당 현장의 모든 점검에 대해 무효화
    queryClient.invalidateQueries({
      predicate: (query) => {
        // previousFindings 쿼리만 무효화
        return query.queryKey[0] === 'previousFindings';
      },
    });

    // 점검 목록도 새로고침 (상태가 변경됐으므로)
    triggerInspectionListRefresh();
  }, [queryClient, triggerInspectionListRefresh]);

  // 인증 상태에 따른 연결/해제
  useEffect(() => {
    if (isUserAuthenticated) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      // 컴포넌트 언마운트 시 연결 해제하지 않음
      // (브라우저 새로고침/탭 이동 등에서 불필요한 재연결 방지)
    };
  }, [isUserAuthenticated, connect, disconnect]);

  // 연결 해제 시 prevOrgIdRef 초기화 (재연결 시 Room 재입장을 위해)
  useEffect(() => {
    if (!isConnected) {
      prevOrgIdRef.current = null;
    }
  }, [isConnected]);

  // WebSocket 연결 성공 시 서버에서 알림 목록 동기화 + Push 구독
  useEffect(() => {
    if (isConnected && isUserAuthenticated && user?.id) {
      console.log('[WebSocket] Connection established, syncing notifications from server...');
      // 사용자가 변경되었으면 알림 초기화 (다른 사용자의 캐시된 알림 제거)
      initializeForUser(user.id);
      fetchNotificationsFromServer();

      // Push 구독 자동 등록 (세션당 1회)
      if (!pushSubscribedRef.current) {
        pushSubscribedRef.current = true;
        const token = tokenManager.getCurrentToken();
        if (token) {
          subscribeToPush(token).catch(() => {
            pushSubscribedRef.current = false;
          });
        }
      }
    }
  }, [isConnected, isUserAuthenticated, user?.id, initializeForUser, fetchNotificationsFromServer]);

  // 재접속 시 전체 캐시 무효화 (끊김 동안 놓친 데이터 동기화)
  const _reconnected = useWebSocketStore((state) => state._reconnected);

  useEffect(() => {
    if (_reconnected) {
      console.log('[WebSocket] Reconnected - invalidating all queries');
      queryClient.invalidateQueries();
      fetchNotificationsFromServer();
      useWebSocketStore.setState({ _reconnected: false });
    }
  }, [_reconnected, queryClient, fetchNotificationsFromServer]);

  // 조직 변경 시 또는 연결 시 Room 입장 (WS room은 companyId 기반)
  // authCurrentMembership은 deps에서 제거 — fetchUser마다 새 객체가 생성되어
  // companyId가 동일해도 effect가 재실행되고 switchOrganization이 불필요하게 재호출됨.
  // companyId 자체로 제어하면 충분.
  useEffect(() => {
    if (isConnected && currentCompanyId && currentCompanyId !== prevOrgIdRef.current) {
      switchOrganization(currentCompanyId);
      prevOrgIdRef.current = currentCompanyId;
    }
  }, [isConnected, currentCompanyId, switchOrganization]);

  // 핸들러들을 ref에 저장 (항상 최신 버전 유지)
  useEffect(() => {
    handlersRef.current = {
      handleMemberRemoved,
      handleMemberInvited,
      handleMemberJoined,
      handleRoleChanged,
      handleOrganizationRestored,
      handleOrgVerificationStatusChanged,
      handleSiteMemberAssigned,
      handleSiteMemberUnassigned,
      handleSitePrimaryNeeded,
      handleSiteUpdated,
      handleSiteAssignmentChanged,
      handleEntityChanged,
      handlePhotoAnalysisStarted,
      handlePhotoAnalysisCompleted,
      handlePhotoAnalysisFailed,
      handlePhotoAdded,
      handlePhotoDeleted,
      handlePhotosDeleted,
      handlePhotoReviewUpdated,
      handlePhotosBulkReviewed,
      handleFindingAdded,
      handleFindingUpdated,
      handleFindingResolved,
      handleFindingDeleted,
      handleFindingRestored,
      handleFindingMeasureAdded,
      handleFindingMeasureRemoved,
      handleFindingLegalAdded,
      handleFindingLegalRemoved,
      handleInspectionReviewCompleted,
      handleInspectionDeleted,
      handleInspectionCreated,
      handleInspectionStatusChanged,
      handleInspectionUpdated,
      handleContributorJoinedRoom,
      handleContributorLeft,
      handleContributorReviewCompleted,
      handleContributorReviewCancelled,
      handleInspectionAllCompleted,
      handleNotificationRead,
      handleNotificationAllRead,
      handleNotificationCleared,
      handleInvitationResponded,
      handleInvitationCancelled,
      handleInvitationRejected,
      handleActionResult,
      handleInspectionModified,
      handleContributorJoinedNotify,
      handleInspectionSoftDeleted,
      handleInspectionRestored,
      handleInspectionCompletedNotify,
      handleReportCreatedNotify,
      handleReportDeletedNotify,
      handleReportCreated,
      handleReportDeleted,
      handlePreviousInspectionCompleted,
      handleNotificationNew,
      handleAIAnalysisStarted,
      handleAIAnalysisCompleted,
      handleAIAnalysisFailed,
      handleUsageUpdated,
    };
  });

  // 멤버 및 조직 이벤트 구독
  // 핸들러 레지스트리 패턴: 소켓 구독은 한 번만 설정, 핸들러는 레지스트리에 저장
  // React 재렌더링이나 effect 재실행 시에도 소켓 구독이 유지됨
  useEffect(() => {
    console.log('[WebSocket] Effect running, isConnected:', isConnected);

    if (!isConnected) {
      console.log('[WebSocket] Not connected, skipping subscription setup...');
      return;
    }

    // 이미 구독이 설정되어 있으면 핸들러만 업데이트 (소켓 구독은 유지)
    const isFirstSetup = !hasSubscription(ServerEvents.MEMBER_LEFT);
    if (isFirstSetup) {
      console.log('[WebSocket] Setting up event subscriptions for the first time...');
    } else {
      console.log('[WebSocket] Subscriptions already exist, updating handlers only...');
    }

    // registerHandler는 소켓 구독이 없으면 생성하고, 핸들러만 레지스트리에 저장
    // 핸들러는 handlersRef를 통해 항상 최신 버전을 호출

    // 멤버 제거 이벤트
    registerHandler<MemberRemovedPayload>(
      ServerEvents.MEMBER_LEFT,
      (data) => {
        console.log('[WebSocket] MEMBER_LEFT wrapper called:', data?.reason);
        handlersRef.current.handleMemberRemoved?.(data);
      }
    );
    // 초대 받음 이벤트
    registerHandler<MemberInvitedPayload>(
      ServerEvents.MEMBER_INVITED,
      (data) => {
        console.log('[WebSocket] MEMBER_INVITED subscription callback triggered:', data);
        handlersRef.current.handleMemberInvited?.(data);
      }
    );
    // 초대 수락됨 이벤트
    registerHandler<MemberJoinedPayload>(
      ServerEvents.MEMBER_JOINED,
      (data) => handlersRef.current.handleMemberJoined?.(data)
    );
    // 역할 변경 이벤트
    registerHandler<MemberRoleChangedPayload>(
      ServerEvents.MEMBER_ROLE_CHANGED,
      (data) => handlersRef.current.handleRoleChanged?.(data)
    );
    // 조직 복구 이벤트
    registerHandler<OrganizationRestoredPayload>(
      ServerEvents.ORGANIZATION_RESTORED,
      (data) => handlersRef.current.handleOrganizationRestored?.(data)
    );
    // 조직 인증 상태 변경 이벤트
    registerHandler<OrganizationVerificationStatusChangedPayload>(
      ServerEvents.ORGANIZATION_VERIFICATION_STATUS_CHANGED,
      (data) => handlersRef.current.handleOrgVerificationStatusChanged?.(data)
    );
    // 현장 배정 이벤트
    registerHandler<SiteMemberAssignedPayload>(
      ServerEvents.SITE_MEMBER_ASSIGNED,
      (data) => {
        console.log('[WebSocket] SITE_MEMBER_ASSIGNED subscription callback triggered:', data);
        handlersRef.current.handleSiteMemberAssigned?.(data);
      }
    );
    // 현장 배정 해제 이벤트
    registerHandler<SiteMemberAssignedPayload>(
      ServerEvents.SITE_MEMBER_UNASSIGNED,
      (data) => handlersRef.current.handleSiteMemberUnassigned?.(data)
    );
    // 정 담당자 지정 필요 이벤트
    registerHandler<SitePrimaryNeededPayload>(
      ServerEvents.SITE_PRIMARY_NEEDED,
      (data) => handlersRef.current.handleSitePrimaryNeeded?.(data)
    );
    // 현장 정보 수정 이벤트 (정 담당자 수정 시 Owner에게)
    registerHandler<SiteUpdatedPayload>(
      ServerEvents.SITE_UPDATED,
      (data) => handlersRef.current.handleSiteUpdated?.(data)
    );
    // 담당자 유형 변경 이벤트
    registerHandler<SiteAssignmentChangedPayload>(
      ServerEvents.SITE_ASSIGNMENT_CHANGED,
      (data) => handlersRef.current.handleSiteAssignmentChanged?.(data)
    );
    // 엔티티 생성·수정·삭제 브로드캐스트 (현장 created/deleted·하위 리소스·협력업체·그룹·멤버·현장 결재선)
    registerHandler<EntityChangedPayload>(
      ServerEvents.ENTITY_CHANGED,
      (data) => handlersRef.current.handleEntityChanged?.(data)
    );

    // ============================================
    // 점검 관련 이벤트
    // ============================================

    // 사진 분석 시작 이벤트 (재분석 대기/분석 중)
    registerHandler<{ inspectionId: string; photoId: string; analysisStatus: string }>(
      ServerEvents.PHOTO_ANALYSIS_STARTED,
      (data) => handlersRef.current.handlePhotoAnalysisStarted?.(data)
    );
    // 사진 분석 완료 이벤트
    registerHandler<PhotoAnalysisCompletedPayload>(
      ServerEvents.PHOTO_ANALYSIS_COMPLETED,
      (data) => handlersRef.current.handlePhotoAnalysisCompleted?.(data)
    );
    // 사진 분석 실패 이벤트
    registerHandler<PhotoAnalysisFailedPayload>(
      ServerEvents.PHOTO_ANALYSIS_FAILED,
      (data) => handlersRef.current.handlePhotoAnalysisFailed?.(data)
    );
    // 사진 추가 이벤트
    registerHandler<PhotoAddedPayload>(
      ServerEvents.PHOTO_ADDED,
      (data) => handlersRef.current.handlePhotoAdded?.(data)
    );
    // 사진 삭제 이벤트
    registerHandler<PhotoDeletedPayload>(
      ServerEvents.PHOTO_DELETED,
      (data) => handlersRef.current.handlePhotoDeleted?.(data)
    );
    // 사진 일괄 삭제 이벤트
    registerHandler<PhotosDeletedPayload>(
      ServerEvents.PHOTOS_DELETED,
      (data) => handlersRef.current.handlePhotosDeleted?.(data)
    );
    // 사진 검토 상태 변경 이벤트
    registerHandler<PhotoReviewUpdatedPayload>(
      ServerEvents.PHOTO_REVIEW_UPDATED,
      (data) => handlersRef.current.handlePhotoReviewUpdated?.(data)
    );
    // 사진 일괄 검토 완료 이벤트
    registerHandler<PhotosBulkReviewedPayload>(
      ServerEvents.PHOTOS_BULK_REVIEWED,
      (data) => handlersRef.current.handlePhotosBulkReviewed?.(data)
    );
    // 발견사항 추가 이벤트
    registerHandler<FindingAddedPayload>(
      ServerEvents.FINDING_ADDED,
      (data) => handlersRef.current.handleFindingAdded?.(data)
    );
    // 발견사항 수정 이벤트
    registerHandler<FindingUpdatedPayload>(
      ServerEvents.FINDING_UPDATED,
      (data) => handlersRef.current.handleFindingUpdated?.(data)
    );
    // 발견사항 해결 이벤트
    registerHandler<FindingResolvedPayload>(
      ServerEvents.FINDING_RESOLVED,
      (data) => handlersRef.current.handleFindingResolved?.(data)
    );
    // 발견사항 삭제 이벤트
    registerHandler<FindingDeletedPayload>(
      ServerEvents.FINDING_DELETED,
      (data) => handlersRef.current.handleFindingDeleted?.(data)
    );
    // 발견사항 복원 이벤트
    registerHandler<FindingRestoredPayload>(
      ServerEvents.FINDING_RESTORED,
      (data) => handlersRef.current.handleFindingRestored?.(data)
    );
    // 개선대책 추가 이벤트
    registerHandler<FindingMeasureAddedPayload>(
      ServerEvents.FINDING_MEASURE_ADDED,
      (data) => handlersRef.current.handleFindingMeasureAdded?.(data)
    );
    // 개선대책 삭제 이벤트
    registerHandler<FindingMeasureRemovedPayload>(
      ServerEvents.FINDING_MEASURE_REMOVED,
      (data) => handlersRef.current.handleFindingMeasureRemoved?.(data)
    );
    // 관련법규 추가 이벤트
    registerHandler<FindingLegalAddedPayload>(
      ServerEvents.FINDING_LEGAL_ADDED,
      (data) => handlersRef.current.handleFindingLegalAdded?.(data)
    );
    // 관련법규 삭제 이벤트
    registerHandler<FindingLegalRemovedPayload>(
      ServerEvents.FINDING_LEGAL_REMOVED,
      (data) => handlersRef.current.handleFindingLegalRemoved?.(data)
    );
    // 검수 완료 이벤트
    registerHandler<InspectionReviewCompletedPayload>(
      ServerEvents.INSPECTION_REVIEW_COMPLETED,
      (data) => handlersRef.current.handleInspectionReviewCompleted?.(data)
    );
    // 점검 삭제 이벤트
    registerHandler<InspectionDeletedPayload>(
      ServerEvents.INSPECTION_DELETED,
      (data) => {
        console.log('[WebSocket] INSPECTION_DELETED subscription callback triggered:', data);
        handlersRef.current.handleInspectionDeleted?.(data);
      }
    );
    // 점검 생성 이벤트
    registerHandler<InspectionCreatedPayload>(
      ServerEvents.INSPECTION_CREATED,
      (data) => {
        console.log('[WebSocket] INSPECTION_CREATED subscription callback triggered:', data);
        handlersRef.current.handleInspectionCreated?.(data);
      }
    );
    // 점검 상태 변경 이벤트
    registerHandler<InspectionStatusChangedPayload>(
      ServerEvents.INSPECTION_STATUS_CHANGED,
      (data) => handlersRef.current.handleInspectionStatusChanged?.(data)
    );
    // 점검 업데이트 이벤트
    registerHandler<InspectionUpdatedPayload>(
      ServerEvents.INSPECTION_UPDATED,
      (data) => handlersRef.current.handleInspectionUpdated?.(data)
    );

    // ============================================
    // 공동 점검 관련 이벤트
    // ============================================

    // 기여자 합류 이벤트 (Room)
    registerHandler<InspectionContributorJoinedRoomPayload>(
      ServerEvents.INSPECTION_CONTRIBUTOR_JOINED_ROOM,
      (data) => handlersRef.current.handleContributorJoinedRoom?.(data)
    );
    // 참여자 퇴장 이벤트
    registerHandler<InspectionContributorLeftRoomPayload>(
      ServerEvents.INSPECTION_CONTRIBUTOR_LEFT_ROOM,
      (data) => handlersRef.current.handleContributorLeft?.(data)
    );
    // 참여자 검수 완료 이벤트
    registerHandler<InspectionContributorReviewCompletedPayload>(
      ServerEvents.INSPECTION_CONTRIBUTOR_REVIEW_COMPLETED,
      (data) => handlersRef.current.handleContributorReviewCompleted?.(data)
    );
    // 참여자 점검완료 취소 이벤트
    registerHandler<InspectionContributorReviewCancelledPayload>(
      ServerEvents.INSPECTION_CONTRIBUTOR_REVIEW_CANCELLED,
      (data) => handlersRef.current.handleContributorReviewCancelled?.(data)
    );
    // 모든 참여자 점검완료 서명 완료 이벤트
    registerHandler<InspectionAllCompletedPayload>(
      ServerEvents.INSPECTION_ALL_COMPLETED,
      (data) => handlersRef.current.handleInspectionAllCompleted?.(data)
    );

    // ============================================
    // 점검 담당자 알림 이벤트
    // ============================================

    // 점검 자료 수정 알림 (점검 담당자에게 직접 전송)
    registerHandler<InspectionModifiedPayload>(
      ServerEvents.INSPECTION_MODIFIED,
      (data) => handlersRef.current.handleInspectionModified?.(data)
    );
    // 새 기여자 합류 알림 (점검 담당자에게 직접 전송)
    registerHandler<InspectionContributorJoinedPayload>(
      ServerEvents.INSPECTION_CONTRIBUTOR_JOINED,
      (data) => handlersRef.current.handleContributorJoinedNotify?.(data)
    );

    // ============================================
    // 점검 참여자 알림 이벤트
    // ============================================

    // 점검 삭제됨 알림 (참여자에게 직접 전송)
    registerHandler<InspectionSoftDeletedPayload>(
      ServerEvents.INSPECTION_SOFT_DELETED,
      (data) => handlersRef.current.handleInspectionSoftDeleted?.(data)
    );
    // 점검 복원됨 알림 (참여자에게 직접 전송)
    registerHandler<InspectionRestoredPayload>(
      ServerEvents.INSPECTION_RESTORED,
      (data) => handlersRef.current.handleInspectionRestored?.(data)
    );
    // 점검 완료됨 알림 (참여자에게 직접 전송)
    registerHandler<InspectionCompletedNotifyPayload>(
      ServerEvents.INSPECTION_COMPLETED_NOTIFY,
      (data) => handlersRef.current.handleInspectionCompletedNotify?.(data)
    );
    // 보고서 생성됨 알림 (참여자에게 직접 전송)
    registerHandler<ReportCreatedNotifyPayload>(
      ServerEvents.REPORT_CREATED_NOTIFY,
      (data) => handlersRef.current.handleReportCreatedNotify?.(data)
    );
    // 보고서 삭제됨 알림 (참여자에게 직접 전송)
    registerHandler<ReportDeletedNotifyPayload>(
      ServerEvents.REPORT_DELETED_NOTIFY,
      (data) => handlersRef.current.handleReportDeletedNotify?.(data)
    );
    // 보고서 생성됨 UI 업데이트 (점검/조직 페이지 새로고침용, 토스트 없음)
    registerHandler<ReportCreatedNotifyPayload>(
      ServerEvents.REPORT_CREATED,
      (data) => handlersRef.current.handleReportCreated?.(data)
    );
    // 보고서 삭제됨 UI 업데이트 (점검/조직 페이지 새로고침용, 토스트 없음)
    registerHandler<ReportDeletedNotifyPayload>(
      ServerEvents.REPORT_DELETED,
      (data) => handlersRef.current.handleReportDeleted?.(data)
    );

    // ============================================
    // 이전 점검 상태 변경 이벤트
    // ============================================

    // 이전 점검 상태 변경 (같은 현장의 다른 점검에서 이전 발견사항 갱신)
    registerHandler<PreviousInspectionCompletedPayload>(
      ServerEvents.PREVIOUS_INSPECTION_COMPLETED,
      (data) => handlersRef.current.handlePreviousInspectionCompleted?.(data)
    );

    // ============================================
    // 알림 이벤트
    // ============================================

    // 새 알림 수신 (서버에서 DB 알림 생성 시)
    registerHandler<{
      id: string;
      type: string;
      title: string;
      message: string;
      resourceType?: string;
      resourceId?: string;
      createdAt: string;
    }>(
      ServerEvents.NOTIFICATION_NEW,
      (data) => {
        console.log('[WebSocket] NOTIFICATION_NEW subscription callback triggered:', data);
        handlersRef.current.handleNotificationNew?.(data);
      }
    );
    // 알림 읽음 동기화
    registerHandler<NotificationReadPayload>(
      ServerEvents.NOTIFICATION_READ,
      (data) => handlersRef.current.handleNotificationRead?.(data)
    );
    // 모든 알림 읽음 동기화
    registerHandler<NotificationAllReadPayload>(
      ServerEvents.NOTIFICATION_ALL_READ,
      (data) => handlersRef.current.handleNotificationAllRead?.(data)
    );
    // 모든 알림 삭제 동기화
    registerHandler<NotificationClearedPayload>(
      ServerEvents.NOTIFICATION_CLEARED,
      (data) => handlersRef.current.handleNotificationCleared?.(data)
    );

    // ============================================
    // 초대 응답 동기화 이벤트
    // ============================================

    // 초대 수락/거절 동기화 (본인의 다른 세션에서 응답함)
    registerHandler<InvitationRespondedPayload>(
      ServerEvents.INVITATION_RESPONDED,
      (data) => handlersRef.current.handleInvitationResponded?.(data)
    );

    // 초대 취소됨 (초대받은 사용자에게 알림)
    registerHandler<InvitationCancelledPayload>(
      ServerEvents.INVITATION_CANCELLED,
      (data) => handlersRef.current.handleInvitationCancelled?.(data)
    );

    // 초대 거절됨 (Owner에게 알림)
    registerHandler<InvitationRejectedPayload>(
      ServerEvents.INVITATION_REJECTED,
      (data) => handlersRef.current.handleInvitationRejected?.(data)
    );

    // ============================================
    // 사용자 액션 결과 이벤트
    // ============================================

    // 사용자 액션 결과 (본인의 모든 세션에 토스트 표시)
    registerHandler<ActionResultPayload>(
      ServerEvents.ACTION_RESULT,
      (data) => {
        console.log('[WebSocket] ACTION_RESULT wrapper called:', data?.action);
        handlersRef.current.handleActionResult?.(data);
      }
    );

    // ============================================
    // AI 분석 이벤트 (점검 단위 - 세션 기반)
    // ============================================

    // AI 분석 시작 (담당자에게 알림)
    registerHandler<AIAnalysisStartedPayload>(
      ServerEvents.AI_ANALYSIS_STARTED,
      (data) => {
        console.log('[WebSocket] AI_ANALYSIS_STARTED wrapper called:', data?.inspectionId);
        handlersRef.current.handleAIAnalysisStarted?.(data);
      }
    );

    // AI 분석 완료 (담당자에게 알림)
    registerHandler<AIAnalysisCompletedPayload>(
      ServerEvents.AI_ANALYSIS_COMPLETED,
      (data) => {
        console.log('[WebSocket] AI_ANALYSIS_COMPLETED wrapper called:', data?.inspectionId);
        handlersRef.current.handleAIAnalysisCompleted?.(data);
      }
    );

    // AI 분석 실패 (담당자에게 알림)
    registerHandler<AIAnalysisFailedPayload>(
      ServerEvents.AI_ANALYSIS_FAILED,
      (data) => {
        console.log('[WebSocket] AI_ANALYSIS_FAILED wrapper called:', data?.inspectionId);
        handlersRef.current.handleAIAnalysisFailed?.(data);
      }
    );

    // ============================================
    // 일일 사용량 관련
    // ============================================

    // 사용량 업데이트 (실시간 동기화)
    registerHandler<UsageUpdatedPayload>(
      ServerEvents.USAGE_UPDATED,
      (data) => {
        console.log('[WebSocket] USAGE_UPDATED received:', data);
        handlersRef.current.handleUsageUpdated?.(data);
      }
    );

    if (isFirstSetup) {
      console.log('[WebSocket] Event subscriptions set up complete');
    }

    // 핸들러 레지스트리 패턴에서는 cleanup이 필요 없음
    // 소켓 구독은 연결 해제 시 자동으로 정리됨 (store의 clearAllSubscriptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, registerHandler, hasSubscription]);

  // Service Worker 등록 (마운트 시 1회)
  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Service Worker 메시지 수신 핸들러
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleSWMessage = (event: MessageEvent) => {
      const { type, payload, url } = event.data || {};

      if (type === 'PUSH_RECEIVED') {
        // Push 데이터 수신 (앱 포커스 중)
        const wsConnected = useWebSocketStore.getState().isConnected;
        if (!wsConnected) {
          // WS 끊김 상태 → push 데이터로 토스트 표시
          showToast(payload.body || payload.message, 'info');
        }
        // WS 연결 중이면 무시 (이미 WS로 처리됨)
      }

      if (type === 'NOTIFICATION_CLICK') {
        // 알림 클릭 → 해당 페이지로 이동
        if (url) {
          navigate(url);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    };
  }, [showToast, navigate]);

  return <>{children}</>;
}
